/* eslint-disable no-nested-ternary */
import React, { useState, useMemo } from "react";
import { Box, Typography, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";

import { UserMode } from "../../../../../interfaces/User";
import { IShift } from "../../../../../interfaces/ShiftInterface";
import { formatTime12hr } from "../../../../../helpers/formatters";
import { IShiftAssignment } from "../../../../../interfaces/ShiftAssignmentInterface";

interface ShiftCellProps {
  dayDate: string;
  allAssignments: IShiftAssignment[];
  employeeId: number;
  authenticable: UserMode;
  defaultShiftId: number | null;
  shifts: IShift[];
  onAssignShift: (shiftId: number, date: string) => void;
  onDeleteShift: (assignmentId: number) => void;
  defaultWeeklyOffs: number[];
}

// eslint-disable-next-line import/prefer-default-export
export function ShiftCell({
  dayDate,
  allAssignments,
  employeeId,
  authenticable,
  defaultShiftId,
  shifts,
  onAssignShift,
  onDeleteShift,
  defaultWeeklyOffs
}: ShiftCellProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const assignment = useMemo(
    () =>
      allAssignments.find((a) => {
        const isTarget =
          authenticable === UserMode.Employee
            ? Number(a.employeeId) === Number(employeeId)
            : Number(a.serviceProviderId) === Number(employeeId);
        if (!isTarget) return false;

        const effDate = moment(a.effectiveDate).format("YYYY-MM-DD");
        const endDate = a.endDate ? moment(a.endDate).format("YYYY-MM-DD") : effDate;

        return dayDate >= effDate && dayDate <= endDate;
      }),
    [allAssignments, authenticable, employeeId, dayDate]
  );

  const assignedShift = assignment
    ? shifts.find((s) => Number(s.id) === Number(assignment.shiftId))
    : null;
  const defaultShift = defaultShiftId
    ? shifts.find((s) => Number(s.id) === Number(defaultShiftId))
    : null;

  const isWeeklyOff = defaultWeeklyOffs.includes(moment(dayDate).day());
  const showWeeklyOff = !assignedShift && isWeeklyOff;
  const displayShift = assignedShift || (!isWeeklyOff ? defaultShift : null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        verticalAlign: "middle",
        position: "relative",
        minHeight: "60px",
        height: "100%",
        p: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "40px"
        }}
      >
        {displayShift || showWeeklyOff ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={assignedShift ? "bold" : "regular"}
                color={
                  assignedShift ? "primary.main" : showWeeklyOff ? "textSecondary" : "textPrimary"
                }
              >
                {showWeeklyOff ? "Weekly Off" : displayShift?.name}{" "}
                {assignedShift || showWeeklyOff ? "" : "(Default)"}
              </Typography>
              {assignedShift && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!assignment?.id) return;
                    onDeleteShift(Number(assignment.id));
                  }}
                  sx={{ p: 0 }}
                >
                  <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              )}
            </Box>
            {displayShift && (
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: "0.65rem" }}>
                {formatTime12hr(displayShift.startTime)} - {formatTime12hr(displayShift.endTime)}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="textSecondary">
            No shift
          </Typography>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={handleOpenMenu}
        sx={{ position: "absolute", bottom: 2, right: 2 }}
      >
        <MoreVertIcon sx={{ fontSize: "1rem" }} />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem disabled>
          <Typography variant="caption" fontWeight="bold">
            Assign Shift
          </Typography>
        </MenuItem>
        {shifts.map((shift) => (
          <MenuItem
            key={shift.id}
            onClick={() => {
              onAssignShift(Number(shift.id), dayDate);
              handleCloseMenu();
            }}
          >
            {shift.name} ({formatTime12hr(shift.startTime)} - {formatTime12hr(shift.endTime)})
          </MenuItem>
        ))}
        {shifts.length === 0 && <MenuItem disabled>No shifts configured</MenuItem>}
      </Menu>
    </Box>
  );
}
